import React, { useEffect, useState, useRef, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { useMembership } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';
import { updateSessionDetail, useDomain } from '@techstyle/redux-core';

import { trackPairItWithClick } from '../../actions/products';
import { generateCdnImagesBmig } from '../../utils/generateCdnImageUrl';
import { getWearItWithProducts } from '../../utils/getWearItWithProducts';
import { isClearanceItem } from '../../utils/selectors';
import { Component as ProductRecommendations } from '../ProductRecommendations';

const StyledProductRecommendations = styled(ProductRecommendations)`
  @media (min-width: ${props => props.theme.breakpoints.large}px) {
    .slick-list {
      margin: 0px 33px ${props => props.theme.spacing.tiny}px;
    }
    .slick-slide {
      > div {
        padding: 0 0 0 ${props => props.theme.spacing.tiny}px;
        text-align: left;
      }
    }
    .slick-arrow {
      font-size: 4px;
      color: ${props => props.theme.colors.active};
      border-radius: 14.4px;
      border: solid 1px ${props => props.theme.colors.lavender250};
      background-color: ${props => props.theme.colors.white};
      margin-top: -25px;
      width: 25px;
      height: 25px;
      text-align: center;
    }
    svg {
      width: 10px;
      height: 12px;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.large - 1}px) {
    .slick-list {
      overflow: visible;
    }
    .slick-slider {
      padding-bottom: 40px; /* Account for dots */
      margin-left: ${props =>
        props.theme.spacing.tiny * -1}px; /* Account for slide margin */
    }
    .slick-slide {
      > div {
        padding: 0 4px;
        text-align: left;
      }
    }
  }

  .slick-dots li.slick-active {
    button:before {
      background: ${props => props.theme.colors.default};
      /* Intensify the light border in case we're on top of a dark image. */
      box-shadow: 0 0 0 1px ${props => props.theme.colors.default};
    }
  }

  .slick-disabled {
    display: none !important;
    pointer-events: none;
  }

  .slick-dots {
    display: contents !important;
  }
  .slick-arrow {
    top: 38%;
  }
  .slick-disabled {
    display: block !important;
  }
  .slick-list {
    margin-bottom: ${props => props.theme.spacing.tiny}px;
  }

  ${props =>
    props.isOnlySingleItem &&
    `
    .slick-track{
      width: unset !important;
    }
    .slick-active{
      width: unset !important;
    }
    .slick-list{
      margin: 0;
    }

    & ${ProductRecommendations.CarouselWrapper}{
      @media (max-width: ${props.theme.breakpoints.large}px) {
        width: 204px;
        margin-left: 0;
      }
      @media (max-width: ${props.theme.breakpoints.medium}px) {
        width: calc(50% + 25px);
      }
      width: 230px;
    }
  `}
`;

function PairItWithRecommendations({
  className,
  hidePrice,
  hidePromo,
  maxProducts,
  options,
  product,
  title,
  trackingSource,
  pSource,
}) {
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const isSaleItem = useSelector(state => isClearanceItem(state, { product }));
  const dispatch = useDispatch();
  const carouselRef = useRef();
  const { isVip } = useMembership();
  const { tld } = useDomain();

  useEffect(() => {
    const wearItWithProducts = getWearItWithProducts(product, {
      domain: tld,
      isVip,
      maxProducts,
      isSaleItem,
    }).map(product => {
      return {
        ...product,
        product_images: generateCdnImagesBmig(product),
      };
    });
    setRecommendedProducts(wearItWithProducts);
  }, [isSaleItem, isVip, maxProducts, product, tld]);

  useEffect(() => {
    // Set carousel to slide 0 if there are changes in the products
    if (recommendedProducts && carouselRef.current) {
      carouselRef.current.slickGoTo(0);
    }
  }, [recommendedProducts]);

  const handleProductClick = useCallback(
    product => {
      dispatch(
        updateSessionDetail({
          name: 'pair_with_click',
          value: '',
        })
      );
      dispatch(trackPairItWithClick(product.label));
    },
    [dispatch]
  );

  const isProductSet =
    product && product.products_in_set && product.products_in_set.length;
  const noItemsAfterFiltering = recommendedProducts.length === 0;
  const isOnlySingleItem = recommendedProducts.length === 1;

  if (isProductSet || !recommendedProducts || noItemsAfterFiltering) {
    return null;
  }

  return (
    <StyledProductRecommendations
      className={className}
      carouselElementRef={carouselRef}
      hidePrice={hidePrice}
      hidePromo={hidePromo}
      isOnlySingleItem={isOnlySingleItem}
      maxProducts={maxProducts}
      onProductClick={handleProductClick}
      options={options}
      product={product}
      products={recommendedProducts}
      pSource={pSource}
      shouldShowSpacedBorder
      shouldUseBmigComponents
      shouldUsePairItWith
      title={title}
      trackingSource={trackingSource}
      variantStyle
    />
  );
}

PairItWithRecommendations.propTypes = {
  className: PropTypes.string,
  hidePrice: PropTypes.bool,
  hidePromo: PropTypes.bool,
  maxProducts: PropTypes.number,
  options: PropTypes.object,
  product: PropTypes.object,
  pSource: PropTypes.string,
  title: PropTypes.node,
  trackingSource: PropTypes.string,
};

PairItWithRecommendations.defaultProps = {
  hidePrice: true,
  maxProducts: 4,
  pSource: 'pair_it_with',
  title: (
    <FormattedMessage
      id="site_pdp.pair_it_with_title"
      defaultMessage="Pair It With"
    />
  ),
};

export default PairItWithRecommendations;
